/**
 * Utilities specific to CTCPAS
 */

/**
 * Dependencies
 */
import isExternalURL from '../../vendor/imarc/inkwell/resources/scripts/modules/linkObserver';
import convertToLocalTime from '../../vendor/imarc/inkwell/resources/scripts/modules/datetimeHandler';

const timeElements = [...document.querySelectorAll('time')];

window.addEventListener('content-populated', () => {
    const anchors = document.querySelectorAll('a');

    if (anchors.length) {
        anchors.forEach(anchor => {
            if (isExternalURL(anchor)) {
                anchor.classList.add('js-external');
            }
        });
    }

    timeElements.forEach(convertToLocalTime);
});

/**
 * Wufoo specific resize logic for htmlEmbed module
 *
 * Ensures the container around the Wufoo iframe is the proper height.
 */
(function () {
  // Sync a wufoo-* container's height with its iframe's height attribute
  function setupIframeHeightSync(container, iframe) {
    // If we already wired this iframe for this container, skip
    if (iframe.__wufooHeightObserverAttached) return;
    iframe.__wufooHeightObserverAttached = true;

    function applyHeightFromAttribute() {
      const heightAttr = iframe.getAttribute("height");
      if (!heightAttr || isNaN(heightAttr)) return;

      const newHeight = parseInt(heightAttr, 10);
      if (!isNaN(newHeight)) {
        container.style.height = newHeight + "px";
      }
    }

    // Apply immediately if there's already a height
    applyHeightFromAttribute();

    // Watch for changes to the iframe's height attribute
    const attrObserver = new MutationObserver(function (mutations) {
      for (const mutation of mutations) {
        if (mutation.type === "attributes" && mutation.attributeName === "height") {
          applyHeightFromAttribute();
        }
      }
    });

    attrObserver.observe(iframe, {
      attributes: true,
      attributeFilter: ["height"]
    });
  }

  // Wait for an iframe to appear inside the container, then sync its height
  function watchForIframe(container) {
    // If an iframe is already there, just set it up
    const existingIframe = container.querySelector("iframe");
    if (existingIframe) {
      setupIframeHeightSync(container, existingIframe);
      return;
    }

    // Otherwise, observe for one being added
    const mo = new MutationObserver(function (mutations, observer) {
      const iframe = container.querySelector("iframe");
      if (iframe) {
        setupIframeHeightSync(container, iframe);
        observer.disconnect();
      }
    });

    mo.observe(container, {
      childList: true,
      subtree: true
    });
  }

  // Process all wufoo-* containers currently in the DOM
  function processWufooContainers() {
    const containers = document.querySelectorAll('[id^="wufoo-"]');

    containers.forEach(function (container) {
      // Avoid wiring the same container multiple times across re-inits
      if (container.__wufooProcessed) return;
      container.__wufooProcessed = true;

      // Make sure the container doesn't scroll
      container.style.overflow = "hidden";

      watchForIframe(container);
    });
  }

  function init() {
    processWufooContainers();

    // Also re-run this logic whenever 'peninsula-html-teardown' fires on body
    if (document.body) {
      document.body.addEventListener("peninsula-html-teardown", function () {
        processWufooContainers();
      });
    } else {
      // In unusual cases where body isn't ready yet, retry once body exists
      const bodyObserver = new MutationObserver(function () {
        if (document.body) {
          document.body.addEventListener("peninsula-html-teardown", function () {
            processWufooContainers();
          });
          bodyObserver.disconnect();
        }
      });
      bodyObserver.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
